import type { Action, ThunkAction } from '@reduxjs/toolkit';
import type { ComponentDescription, ComponentInstance } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { produce } from 'immer';
import type { AppProjectEditorRootState } from '../../reducer';
import {
  selectCurrentView,
  selectCurrentViewComponentInstancesPathsByIds,
  selectDolanComponentDescriptionsByName,
} from '../../dolan/selectors';
import editCurrentDolan from '../../dolan/actions/editCurrentDolan';
import getComponentDescriptionsByName from '../../dolan/utils/getComponentDescriptionsByName';
import { pickCompositeRootInnerStyles } from '../../../modules/styles';
import getComponentInstanceLogicFlowsFromView from '../utils/getComponentInstanceLogicFlowsFromView';
import { ComponentTemplate } from '../types';
import { omitUndefinedProperties } from '../../utils/objects';
import resetComponentDescriptionInstances from '../utils/resetComponentDescriptionInstances';
import migrateComponentDescription from '../../dolan/utils/migrateComponentDescription';
import installComponentDescription from '../utils/installComponentDescription';
import editComponentInstanceInViewById from '../utils/editComponentInstanceInViewById';
import migrateComponentInstance from '../utils/migrateComponentInstance';

/**
 * Action thunk that applies the current state of the currently isolated component instance to the component description.
 * It will then update all the existing instances of that description to match the new state.
 */
export default function commitIsolatedComponentInstanceChangesToDescription(
  isolatedComponentInstance: ComponentInstance,
): ThunkAction<ComponentDescription, AppProjectEditorRootState, unknown, Action<string>> {
  return (dispatch, getState) => {
    const { name } = isolatedComponentInstance;
    const initialState = getState();
    const descriptionsByName = selectDolanComponentDescriptionsByName(initialState);
    const componentInstancePathsById = selectCurrentViewComponentInstancesPathsByIds(initialState);
    const isolatedComponentInstancePath = componentInstancePathsById[isolatedComponentInstance.id];
    const isolatedDescription = descriptionsByName[name];
    const currentView = selectCurrentView(initialState);
    if (!isolatedDescription || !isolatedComponentInstancePath) {
      throw new Error(`No isolated component instance`);
    }
    if (!currentView) {
      throw new Error(`No current view`);
    }
    const migratedIsolatedComponentInstance = migrateComponentInstance(isolatedComponentInstance);
    const compositeTemplateRootStyle = pickCompositeRootInnerStyles(migratedIsolatedComponentInstance.style);
    const compositeTemplateInstance = produce(migratedIsolatedComponentInstance, (draft) => {
      draft.style = compositeTemplateRootStyle;
      delete draft.style_class;
      delete draft.repeat;
      delete draft.nested_overrides;
    });
    const compositeTemplateLogicFlows = getComponentInstanceLogicFlowsFromView(
      migratedIsolatedComponentInstance,
      currentView,
      'inner',
    );
    const compositeTemplate: ComponentTemplate = {
      component: compositeTemplateInstance,
      flows: compositeTemplateLogicFlows,
    };
    const newDescription = migrateComponentDescription(
      omitUndefinedProperties<ComponentDescription>({
        ...isolatedDescription,
        group: undefined,
        composite_json: JSON.stringify(compositeTemplate),
        version: -1,
      }),
    );
    const editedDolan = dispatch(
      editCurrentDolan((dolan) => {
        // Update the description
        let newDolan = installComponentDescription(dolan, newDescription);
        // Update the isolated component instance
        newDolan = editComponentInstanceInViewById(
          newDolan,
          currentView.id,
          isolatedComponentInstance.id,
          () => migratedIsolatedComponentInstance,
        );
        // Replace all instances of the description, except this isolated instance
        return resetComponentDescriptionInstances(newDolan, newDescription, [isolatedComponentInstance.id]);
      }),
    );
    // Return the updated description
    const descriptions = getComponentDescriptionsByName(editedDolan);
    return descriptions[name];
  };
}
